<script setup lang="ts">
import { ref } from 'vue';
import omit from 'lodash/omit';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { formatDate } from '@/utils/dateUtils';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import FlightDetail from './FlightDetail.vue';
import SortButtons from './SortButtons.vue';
import PricingTable from './PricingTable.vue';

interface Props {
  listTour?: any;
  toursSelected?: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{ applyPricing: [value: any] }>();

const lang = getCurrentLocale();
const { handleReplaceValueToName } = useConvertTextComposable();

const selectedRooms = ref([]);
const unselectedRoomsGreaterZero = ref([]);
const unselectedRoomsZero = ref([]);
const isSortByName = ref(false);
const isSortBySinglePrice = ref(false);
const isSortByDoublePrice = ref(false);
const listTourData = ref<any>([]);
const tourSelected = ref(props.toursSelected);

const getListRoomSelected = (listTour: any) => {
  return listTour
    ?.filter((tour: any) => tour.ttsId === tourSelected.value.ttsId)
    .map((tour: any) => {
      return {
        ...tour,
        isSelected: true
      };
    });
};

const handleExpendedRow = (list: any, init: boolean) => {
  return list.map((tour: any, index: number) => {
    return {
      ...tour,
      tourNameEn: props.toursSelected?.tourNameEn,
      tourNameFr: props.toursSelected?.tourNameFr,
      tourDescriptionEn: props.toursSelected?.tourDescriptionEn,
      tourDescriptionFr: props.toursSelected?.tourDescriptionFr,
      tourInclusionEn: props.toursSelected?.tourInclusionEn,
      tourInclusionFr: props.toursSelected?.tourInclusionFr,
      tourDurationEn: props.toursSelected?.tourDurationEn,
      tourDurationFr: props.toursSelected?.tourDurationFr,
      tourType: props.toursSelected?.tourType,
      guided: props.toursSelected?.guided,
      isExpended: init ? index === 0 : tour?.isExpended
    };
  });
};

const getTotalPricing = (priceValue) => {
  const cloneData = omit(priceValue, ['childPrices']);
  return Object.values(cloneData).reduce((acc, value) => acc + parseFloat(value), 0);
};

const getOtherZero = (listTour: any) => {
  const listTourData = listTour?.filter((tour: any) => {
    return getTotalPricing(tour.rooms[0].price.sellingPrice) === 0;
  });
  return listTourData
    .filter((tour: any) => {
      return !getListRoomSelected(listTour).some((tourSelected: any) => tourSelected.ttsId === tour.ttsId);
    })
    .map((tour: any) => {
      return {
        ...tour,
        isSelected: false
      };
    });
};

const getOtherGreaterZero = (listTour: any) => {
  const listTourData = listTour?.filter((tour: any) => {
    return getTotalPricing(tour.rooms[0].price.sellingPrice) > 0;
  });
  return listTourData
    .filter((tour: any) => {
      return !getListRoomSelected(listTour).some((tourSelected: any) => tourSelected.ttsId === tour.ttsId);
    })
    .map((tour: any) => {
      return {
        ...tour,
        isSelected: false
      };
    });
};

const handleSort = (listTour: any, init: boolean) => {
  selectedRooms.value = getListRoomSelected(listTour);
  unselectedRoomsGreaterZero.value = getOtherGreaterZero(listTour);
  unselectedRoomsZero.value = getOtherZero(listTour);
  return handleExpendedRow([...selectedRooms.value, ...unselectedRoomsGreaterZero.value, ...unselectedRoomsZero.value], init);
};

const expandPricingFunc = (index: number) => {
  listTourData.value[index].isExpended = !listTourData.value[index].isExpended;
};

const sortByName = (tourList: any) => {
  return tourList?.sort((tourFirst: any, tourSecond: any) => {
    const tourNameFirst = getTextVariant(lang, tourFirst?.tourNameEn, tourFirst?.tourNameFr);
    const tourNameSecond = getTextVariant(lang, tourSecond?.tourNameEn, tourSecond?.tourNameFr);
    const nameFirst = tourNameFirst.toLowerCase();
    const nameSecond = tourNameSecond.toLowerCase();
    return nameFirst.localeCompare(nameSecond);
  });
};

const sortByPrice = (tourList: any, type: string) => {
  return tourList?.sort((tourFirst: any, tourSecond: any) => {
    const priceFirst = parseFloat(tourFirst.rooms[0].price.sellingPrice[type]);
    const priceSecond = parseFloat(tourSecond.rooms[0].price.sellingPrice[type]);
    if (priceFirst === 0) {
      return 1;
    } else if (priceSecond === 0) {
      return -1;
    }

    return priceFirst - priceSecond;
  });
};

const handleSortByName = (data: boolean) => {
  isSortBySinglePrice.value = false;
  isSortByDoublePrice.value = false;
  isSortByName.value = !data;
  handleSort(listTourData.value, false);
  if (isSortByName.value) {
    listTourData.value = [...sortByName(selectedRooms.value), ...sortByName(unselectedRoomsGreaterZero.value), ...sortByName(unselectedRoomsZero.value)];
  }
};

const handleSortBySinglePrice = (data: boolean) => {
  isSortBySinglePrice.value = !data;
  isSortByDoublePrice.value = false;
  isSortByName.value = false;
  handleSort(listTourData.value, false);
  if (isSortBySinglePrice.value) {
    listTourData.value = [...sortByPrice(selectedRooms.value, 'singlePrice'), ...sortByPrice(unselectedRoomsGreaterZero.value, 'singlePrice'), ...unselectedRoomsZero.value];
  }
};

const handleSortByDoublePrice = (data: boolean) => {
  isSortByDoublePrice.value = !data;
  isSortBySinglePrice.value = false;
  isSortByName.value = false;
  handleSort(listTourData.value, false);
  if (isSortByDoublePrice.value) {
    listTourData.value = [...sortByPrice(selectedRooms.value, 'doublePrice'), ...sortByPrice(unselectedRoomsGreaterZero.value, 'doublePrice'), ...unselectedRoomsZero.value];
  }
};

const selectTour = (tourSelectedData: any) => {
  tourSelected.value = tourSelectedData;
  listTourData.value = listTourData.value.map((tour: any) => {
    return {
      ...tour,
      isSelected: tourSelectedData.ttsId === tour.ttsId
    };
  });
};

const renderTourType = (tour: any) => {
  const tourTypeText = tour.tourType ? ` - ${handleReplaceValueToName(tour.tourType, 'tourType') ?? ''}` : '';
  const guidedText = tour.guided ? ` - ${handleReplaceValueToName(tour.guided?.toUpperCase(), 'guided') ?? ''}` : '';
  return `${tourTypeText}${guidedText}`;
};

const renderHeaderTable = (tour: any) => {
  return `(${formatDate(tour.depDate, 'dd-LLL-yyyy', false, lang)}) ${getTextVariant(lang, tour?.tourNameEn, tour?.tourNameFr)} - ${getTextVariant(lang, tour?.rooms[0].longName, tour?.rooms[0].longNameFr)} (${getTextVariant(lang, tour?.tourDurationEn, tour?.tourDurationFr)})${renderTourType(tour)} (${tour?.ttsId}) `;
};

const handleCloseAndApply = () => {
  const tourApply = listTourData.value.find((tour: any) => tour.ttsId === tourSelected.value.ttsId);
  delete tourApply.isSelected;
  delete tourApply.isExpended;
  emits('applyPricing', tourApply);
};

listTourData.value = handleSort(props.listTour, true);
</script>
<template>
  <div>
    <SortButtons
      :isSortByName="isSortByName"
      :isSortBySinglePrice="isSortBySinglePrice"
      :isSortByDoublePrice="isSortByDoublePrice"
      @handleSortByName="handleSortByName(isSortByName)"
      @handleSortBySinglePrice="handleSortBySinglePrice(isSortBySinglePrice)"
      @handleSortByDoublePrice="handleSortByDoublePrice(isSortByDoublePrice)"
    />
    <div v-for="(tour, indexTour) in listTourData" :key="indexTour">
      <div>
        <div class="lead-pricing flex align-items-center justify-content-between mt-3">
          <div class="flex gap-2">
            <div class="title">{{ renderHeaderTable(tour) }}</div>
            <div class="flex align-items-center cursor-pointer expand-tour" @click="expandPricingFunc(indexTour)">[&nbsp; <i class="pi pi-minus" v-if="tour.isExpended"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
          </div>
          <div><Checkbox v-model="tour.isSelected" @change="selectTour(tour)" :binary="true" /></div>
        </div>
        <div v-show="tour.isExpended" class="pl-3 pr-3">
          <div>
            <FlightDetail isView :tour="tour" />
          </div>
          <div class="lead-pricing">{{ $t('common.pricing') }}</div>
          <PricingTable isView :pricing="tour.rooms[0].price" :depDate="tour.rooms[0].depDate" :additionalTax="tour.rooms[0].additionalTax" />
        </div>
      </div>
    </div>
    <div class="text-center mt-2 w-full">
      <Button id="apply-btn" icon="pi pi-check" :label="$t('common.buttons.close_apply')" @click="handleCloseAndApply()" />
    </div>
  </div>
</template>
<style scoped lang="scss">
.lead-pricing {
  background-color: rgb(115, 115, 115);
  color: white;
  padding: 0.8rem 1.2rem;
  font-weight: 700;
}
</style>
